/**
 * ลำดับขีด — โหลดข้อมูลจาก public/data/strokes-*.json
 *
 * kana กับ radical อยู่ไฟล์เดียว (gzip 4 KB) เพราะใช้ทุกวันตั้งแต่วันแรก
 * คันจิแบ่งเป็นชุดละ 256 ตัว โหลดเฉพาะชุดที่ถึงคิว — การ์ดบอกเลขชุดมาเอง
 * ผ่าน extra.shard (ดู decks/kanji.ts)
 *
 * service worker cache /data/*.json แบบ CacheFirst อยู่แล้ว จึงไม่ต้องเก็บลง
 * IndexedDB ซ้ำ — โหลดครั้งเดียวต่อชุด แล้วอยู่ในเครื่องถาวร
 */
import type { Card } from './types'

/** ทุกตัวใน KanjiVG วาดในกรอบ 109×109 */
export const STROKE_BOX = 109

/** ต้องตรงกับ SHARD_SIZE ใน scripts/build-strokes.ts */
export const STROKE_SHARD_SIZE = 256

export interface StrokeChar {
  /** path data ของแต่ละขีด เรียงตามลำดับเขียน */
  d: string[]
  /** ชิ้นส่วน [ตัวอักษร, ขีดแรก, จำนวนขีด, ตำแหน่ง] — ไม่มีถ้าแตกไม่ได้ */
  p?: Array<[string, number, number, string]>
}

type Bundle = Record<string, StrokeChar>

const BASE = import.meta.env.BASE_URL
const loaded = new Map<string, Promise<void>>()
const chars = new Map<string, StrokeChar>()

function fetchBundle(name: string): Promise<void> {
  let p = loaded.get(name)
  if (!p) {
    p = fetch(`${BASE}data/${name}.json`)
      .then((res): Promise<Bundle> => (res.ok ? (res.json() as Promise<Bundle>) : Promise.resolve({})))
      .then((b) => {
        for (const [ch, s] of Object.entries(b)) chars.set(ch, s)
      })
      .catch(() => undefined)
    loaded.set(name, p)
  }
  return p
}

/** ชุดที่การ์ดใบนี้อยู่ — kana/radical ไม่มีเลขชุด อยู่ core หมด */
export function shardOf(card: Card): string {
  const s = card.extra?.['shard']
  return typeof s === 'number' ? `strokes-${s}` : 'strokes-core'
}

/**
 * โหลดล่วงหน้าตอนเริ่ม session — ปุ่มจะได้กดแล้วขึ้นทันที ไม่ต้องรอเน็ต
 * เรียกซ้ำได้ ชุดที่โหลดแล้วไม่โหลดอีก
 */
export function prefetchStrokes(cards: readonly Card[]): void {
  const names = new Set<string>(['strokes-core'])
  for (const c of cards) names.add(shardOf(c))
  for (const n of names) void fetchBundle(n)
}

/**
 * ลำดับขีดของตัวอักษรหนึ่งตัว · null = ไม่มีข้อมูล
 * (เหลือแค่ jinmeiyō รูปเก่า 32 ตัวที่ KanjiVG ไม่ได้ทำไว้)
 *
 * bundle บอกว่าให้ลองชุดไหนก่อน — ชิ้นส่วนของคันจิเป็น radical ซึ่งอยู่ core
 * จึงลอง core เป็นทางสำรองเสมอ
 */
export async function strokesFor(ch: string, bundle = 'strokes-core'): Promise<StrokeChar | null> {
  if (chars.has(ch)) return chars.get(ch) ?? null
  await fetchBundle(bundle)
  if (chars.has(ch)) return chars.get(ch) ?? null
  if (bundle !== 'strokes-core') await fetchBundle('strokes-core')
  return chars.get(ch) ?? null
}

/**
 * ลำดับขีดของทุกตัวบนหน้าการ์ด
 * yōon (ちゃ) เป็นสองตัวที่ KanjiVG ทำไฟล์แยก — วาดเรียงกันสองกรอบ
 * null ในอาเรย์ = ตัวนั้นไม่มีข้อมูล ทั้งใบจึงแสดงไม่ได้
 */
export async function strokesForCard(card: Card): Promise<StrokeChar[] | null> {
  const bundle = shardOf(card)
  const out = await Promise.all([...card.front].map((ch) => strokesFor(ch, bundle)))
  return out.every((s): s is StrokeChar => s !== null) ? out : null
}
